import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from '../common/Modal';
import { addWorkspaceMember, getProjects } from '../../services/api';

const AddMemberModal = ({ isOpen, onClose, workspaceId, onMemberAdded }) => {
  const [shareId, setShareId] = useState('');
  const [role, setRole] = useState('member');
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectPermissions, setProjectPermissions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const loadProjectsList = async () => {
      setLoadingProjects(true);
      try {
        const result = await getProjects(50, 0, workspaceId);
        if (result.success) {
          setProjects(result.data || []);
        }
      } catch (error) {
        // Silent error handling
      } finally {
        setLoadingProjects(false);
      }
    };
    if (isOpen) {
      loadProjectsList();
    }
  }, [isOpen, workspaceId]);

  const handleSubmit = async () => {
    setError('');
    setSuccessMessage('');

    if (!shareId.trim()) {
      setError('Please enter a user share ID');
      return;
    }

    const editProjects = Object.entries(projectPermissions)
      .filter(([_, permission]) => permission === 'edit')
      .map(([projectId]) => projectId);
    
    const viewProjects = Object.entries(projectPermissions)
      .filter(([_, permission]) => permission === 'view')
      .map(([projectId]) => projectId);

    setIsLoading(true);
    try {
      const result = await addWorkspaceMember(
        workspaceId, 
        shareId.trim().toUpperCase(), 
        role,
        editProjects,
        viewProjects
      );
      if (result.success) {
        const totalShared = editProjects.length + viewProjects.length;
        setSuccessMessage(
          totalShared > 0 
            ? `Member added with access to ${totalShared} prompt${totalShared > 1 ? 's' : ''}!`
            : 'Member added successfully!'
        );
        if (onMemberAdded) {
          onMemberAdded(result.data);
        }
        setTimeout(() => handleClose(), 800);
      } else {
        setError(result.error || 'Failed to add member');
      }
    } catch (err) {
      setError('Failed to add member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setShareId('');
      setRole('member');
      setProjectPermissions({});
      setSearchQuery('');
      setError('');
      setSuccessMessage('');
      onClose();
    }
  };

  const setProjectPermission = (projectId, permission) => {
    setProjectPermissions(prev => {
      if (permission === 'none') {
        const newPerms = { ...prev };
        delete newPerms[projectId];
        return newPerms;
      }
      return { ...prev, [projectId]: permission };
    });
  };

  const setAllPermissions = (permission) => {
    if (permission === 'none') {
      setProjectPermissions({});
    } else {
      const newPerms = {};
      filteredProjects.forEach(project => {
        const projectId = project.id || project._id;
        newPerms[projectId] = permission;
      });
      setProjectPermissions(newPerms);
    }
  };

  const filteredProjects = projects.filter(project => 
    project.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    project.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button
        onClick={handleClose}
        disabled={isLoading}
        className="modal-cancel-btn"
      >
        Cancel
      </button>
      <button
        onClick={handleSubmit}
        disabled={isLoading || !shareId.trim()}
        className="modal-confirm-btn px-4 py-2.5 text-sm font-medium rounded-lg transition-all inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isLoading && (
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )}
        <span>{isLoading ? 'Adding...' : 'Add Member'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Member to Workspace"
      size="lg"
      footer={footer}
      closeOnOverlayClick={!isLoading}
      closeOnEsc={!isLoading}
    >
      <div className="space-y-5">
        {/* Success Message */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-success-50 border border-primary-500/30 text-primary-500">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>{successMessage}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm bg-error-50 border border-error/30 text-error-400">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span>{error}</span>
          </div>
        )}

        {/* User Share ID Input */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">
            User Share ID <span className="text-error">*</span>
          </label>
          <input
            type="text"
            placeholder="ABC12345"
            value={shareId}
            onChange={(e) => { setShareId(e.target.value.toUpperCase()); if (error) setError(''); }}
            className="w-full px-4 py-3.5 rounded-lg font-mono text-xl tracking-[0.3em] text-center uppercase disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none transition-all bg-primary-500/5 border-2 border-primary-500/20 text-primary-500 focus:border-primary-500 focus:ring-4 focus:ring-primary-500/15"
            maxLength={8}
            disabled={isLoading}
            autoFocus
          />
        </div>

        {/* Role Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">Role</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setRole('member')}
              disabled={isLoading}
              className={`px-4 py-2.5 rounded-lg border-2 transition-all text-center text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                role === 'member'
                  ? 'border-primary-500 bg-success-50 text-primary-500 shadow-glow-sm'
                  : 'border-primary-500/15 bg-transparent text-text-secondary'
              }`}
            >
              Member
            </button>
            <button
              onClick={() => setRole('admin')}
              disabled={isLoading}
              className={`px-4 py-2.5 rounded-lg border-2 transition-all text-center text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed ${
                role === 'admin'
                  ? 'border-primary-500 bg-success-50 text-primary-500 shadow-glow-sm'
                  : 'border-primary-500/15 bg-transparent text-text-secondary'
              }`}
            >
              Admin
            </button>
          </div>
        </div>

        {/* Project Permissions */}
        <div className="space-y-2">
           <div className="flex items-center justify-between">
             <label className="block text-sm font-medium text-text-secondary">
               Prompt Access Permissions
             </label>
            {filteredProjects.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setAllPermissions('none')}
                  className="text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-text-tertiary hover:text-text-secondary"
                  disabled={loadingProjects}
                >
                  Clear All
                </button>
                <button
                  onClick={() => setAllPermissions('view')}
                  className="text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-info hover:text-info-400"
                  disabled={loadingProjects}
                >
                  All View
                </button>
                <button
                  onClick={() => setAllPermissions('edit')}
                  className="text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-primary-500 hover:text-primary-400"
                  disabled={loadingProjects}
                >
                  All Edit
                </button>
              </div>
            )}
          </div>

          {/* Search */}
          {projects.length > 0 && (
            <div className="relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 rounded-lg text-sm focus:outline-none transition-all bg-bg-input border border-primary-500/15 text-text-primary focus:border-primary-500"
              />
            </div>
          )}

          {/* Projects List */}
          <div className="rounded-lg max-h-64 overflow-y-auto bg-bg-input/60 border border-primary-500/15">
            {loadingProjects ? (
              <div className="p-8 text-center">
                <div className="inline-block w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                <p className="text-sm text-text-tertiary">Loading prompts...</p>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 mx-auto mb-2 opacity-50 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <p className="text-sm text-text-tertiary">
                  {searchQuery ? 'No prompts found' : 'No prompts yet'}
                </p>
              </div>
            ) : (
              <div>
                {filteredProjects.map((project, idx) => {
                  const projectId = project.id || project._id;
                  const currentPermission = projectPermissions[projectId] || 'none';
                  
                  return (
                  <div
                    key={projectId}
                    className={`flex items-center gap-3 p-3 transition-colors ${
                      idx < filteredProjects.length - 1 ? 'border-b border-primary-500/8' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate text-text-primary">
                        {project.project_name || project.name}
                      </p>
                      {project.description && (
                        <p className="text-xs truncate mt-0.5 text-text-tertiary">
                          {project.description}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => setProjectPermission(projectId, 'none')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                          currentPermission === 'none'
                            ? 'bg-white/10 border border-white/20 text-text-primary'
                            : 'bg-transparent border border-transparent text-text-tertiary'
                        }`}
                      >
                        None
                      </button>
                      <button
                        onClick={() => setProjectPermission(projectId, 'view')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                          currentPermission === 'view'
                            ? 'bg-info/20 border border-info text-info'
                            : 'bg-transparent border border-transparent text-text-tertiary'
                        }`}
                      >
                        View
                      </button>
                      <button
                        onClick={() => setProjectPermission(projectId, 'edit')}
                        className={`px-2 py-1 text-xs font-medium rounded transition-all ${
                          currentPermission === 'edit'
                            ? 'bg-success-50 border border-primary-500 text-primary-500'
                            : 'bg-transparent border border-transparent text-text-tertiary'
                        }`}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {Object.keys(projectPermissions).length > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-500/5 border border-primary-500/15">
              <svg className="w-4 h-4 text-primary-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <p className="text-xs font-medium text-text-secondary">
                {Object.values(projectPermissions).filter(p => p === 'edit').length} Edit • {' '}
                {Object.values(projectPermissions).filter(p => p === 'view').length} View
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

AddMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  workspaceId: PropTypes.string.isRequired,
  onMemberAdded: PropTypes.func
};

export default AddMemberModal;
